// Types
import {
  InterviewStatus,
  UpcomingInterviewsInput,
  UserRole,
} from 'api/graphql-global-types';
import { GetInterview_getInterview } from 'api/interview/types/GetInterview';
import { GetInterview_getInterview_interviewee_storeDetails } from 'api/interview/types/GetInterview';
// Helpers
import { formatLargeNumber } from './formatLargeNumber';
import { getUserRoleLabel } from './user';
// Const
import {
  MANAGE_INTERVIEWS,
  SCHEDULE_INTERVIEW,
  USER_SCHEDULE_INTERVIEW,
} from 'constants/routes';
// UI3
import { IconNames } from 'ui3/MillionsIcon/MillionsIcon';

export type TalentData = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

export type InterviewType = 'host' | 'guest' | null;

export type InterviewConstantProps = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  mobileTitle: string;
  description: string;
  type: InterviewType;
  icon: IconNames;
  isInverted?: boolean;
};

type InterviewConstantPropsAndKey = InterviewConstantProps & {
  key: string;
};

export const INTERVIEWS: InterviewConstantPropsAndKey[] = [
  {
    key: 'guestInterview',
    imageSrc: '/images/microphone.png',
    imageAlt: 'be interviewed',
    title: 'I want to be interviewed',
    mobileTitle: 'I want to be interviewed',
    description: 'Get interviewed by a host, or let us assign one for you.',
    type: 'guest',
    icon: 'microphoneOutline',
  },
  {
    key: 'hostInterview',
    imageSrc: '/images/microphone_headset.png',
    imageAlt: 'host an interview',
    title: 'I want to host an interview',
    mobileTitle: 'Host an interview',
    description: 'Host your interview, set availability and send invite link.',
    type: 'host',
    icon: 'faceWithHeadphones',
    isInverted: true,
  },
];

type GetInterviewRedirectUrlProp = {
  interview: GetInterview_getInterview;
  isReschedulePage?: boolean;
  isInvitePage?: boolean;
};

export const getInterviewRedirectUrl = ({
  interview,
  isReschedulePage = false,
  isInvitePage = false,
}: GetInterviewRedirectUrlProp): string | false => {
  const hostSlug = interview.interviewer?.slug;
  const streamSlug = interview.stream?.slug;
  const interviewId = interview.id;

  const isHidden = interview.stream?.isHidden;
  const isCanceled = interview.status === InterviewStatus.Canceled;
  const isEnded = interview.status === InterviewStatus.Completed;
  // if trying to schedule an interview from invite page with invited status, we should not redirect (loop)
  const isInvited =
    interview.status === InterviewStatus.Invited && !isInvitePage;
  // if trying to reschedule a scheduled interview, we should not redirect
  const isScheduled =
    interview.status === InterviewStatus.Scheduled && !isReschedulePage;
  // if it's already reschedule page, we don't need to redirect  (loop)
  const isRequested =
    interview.status === InterviewStatus.RescheduleRequested &&
    !isReschedulePage;

  if (isHidden || isCanceled) {
    // hidden and canceled can't be show so we redirect to host's streams
    return `/${hostSlug}/streams`;
  }

  if (isEnded) {
    // ended redirects to that stream/interview's watch page
    return `/${hostSlug}/streams/${streamSlug}`;
  }

  if (isScheduled) {
    // users are redirected to their interviews table (unless action was to reschedule)
    return MANAGE_INTERVIEWS;
  }

  if (isRequested) {
    // users are redirected to the reschedule page (unless they are already on that page)
    return `${USER_SCHEDULE_INTERVIEW}/interview/${interviewId}`;
  }

  if (isInvited) {
    // when invited, users need to be redirected to scheduling page
    return `${SCHEDULE_INTERVIEW}/${interviewId}`;
  }

  // returning false will allow stream notifications with other statuses to run their course
  return false;
};

//  used to fetch soonest rescheduled interview
export const fetchSoonestRescheduleInterviewInput: UpcomingInterviewsInput = {
  statuses: [InterviewStatus.RescheduleRequested],
};

export type SocialLink = {
  type: string;
  url: string;
  icon: IconNames;
};

/**
 * Extracts and formats social media links from guest store details
 * @param guestDetails - Store details of the guest/interviewee
 * @returns Array of formatted social media links
 */
export const getGuestSocialLinks = (
  guestDetails:
    | GetInterview_getInterview_interviewee_storeDetails
    | null
    | undefined
): SocialLink[] => {
  if (!guestDetails) return [];

  const links: SocialLink[] = [];

  // Backend returns full URLs, use them directly
  if (guestDetails.instagramLink?.trim()) {
    links.push({
      type: 'instagram',
      url: guestDetails.instagramLink.trim(),
      icon: 'instagram',
    });
  }

  if (guestDetails.twitterLink?.trim()) {
    links.push({
      type: 'twitter',
      url: guestDetails.twitterLink.trim(),
      icon: 'x',
    });
  }

  if (guestDetails.tiktokLink?.trim()) {
    links.push({
      type: 'tiktok',
      url: guestDetails.tiktokLink.trim(),
      icon: 'tiktok',
    });
  }

  if (guestDetails.facebookLink?.trim()) {
    links.push({
      type: 'facebook',
      url: guestDetails.facebookLink.trim(),
      icon: 'facebook',
    });
  }

  if (guestDetails.youtubeLink?.trim()) {
    links.push({
      type: 'youtube',
      url: guestDetails.youtubeLink.trim(),
      icon: 'youtube',
    });
  }

  return links;
};

/**
 * Extracts unique topics from interview questions
 * @param questions - Array of interview questions
 * @returns Array of unique topic names
 */
export const getInterviewTopics = (
  questions: GetInterview_getInterview['questions'] | null | undefined
): string[] => {
  if (!questions) return [];

  return Array.from(
    new Set(
      questions
        .map((q) => q.topicName)
        .filter((topic): topic is string => Boolean(topic))
    )
  );
};

/**
 * Gets PPV event stream from interview source if available
 * @param interview - Interview data
 * @returns PPV event stream or null
 */
export const getPPVEventFromInterview = (
  interview: GetInterview_getInterview | null | undefined
) => {
  if (
    interview?.source?.__typename === 'InterviewSourcePPVParticipant' &&
    interview.source.stream
  ) {
    return interview.source.stream;
  }
  return null;
};

/**
 * Gets the PPV stream organizer ID from interview if it's a PPV interview without an assigned interviewer
 * This is only used for display purposes (organizer card), not for slot fetching
 * @param interview - Interview data
 * @returns PPV organizer ID or null
 */
export const getPPVOrganizerId = (
  interview: GetInterview_getInterview | null | undefined
): string | null => {
  if (
    interview?.source?.__typename === 'InterviewSourcePPVParticipant' &&
    !interview?.interviewer &&
    interview.source.stream?.store?.id
  ) {
    return interview.source.stream.store.id;
  }
  return null;
};

export type Organizer = {
  id: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  sport: string;
  role: string;
  numberOfAudience: string;
  slug?: string;
};

type OrganizerDataSource = {
  id: string;
  firstName: string;
  lastName: string;
  slug?: string | null;
  role?: UserRole | string;
  audience?: number | null;
  followerCount?: number;
  storeDetails?: {
    avatarURL?: string | null;
  } | null;
  sports?: Array<{
    name?: string | null;
  }>;
};

export const extractOrganizer = (
  data: OrganizerDataSource | null | undefined
): Organizer | undefined => {
  if (!data) {
    return undefined;
  }

  const audienceCount = data?.audience ?? data?.followerCount;

  return {
    id: data.id,
    firstName: data?.firstName,
    lastName: data?.lastName,
    avatarUrl: data?.storeDetails?.avatarURL || undefined,
    sport: data?.sports?.[0]?.name?.toUpperCase() || '',
    numberOfAudience: audienceCount ? formatLargeNumber(audienceCount) : '',
    role: data?.role ? getUserRoleLabel(data?.role as UserRole) : 'Organizer',
    slug: data?.slug || undefined,
  };
};

/**
 * Formats interviewer name from interview data
 * @param interviewer - Interviewer object with firstName and lastName
 * @returns Formatted full name or empty string
 */
export const formatInterviewerName = (
  interviewer:
    | { firstName?: string | null; lastName?: string | null }
    | null
    | undefined
): string => {
  if (!interviewer) return '';
  return `${interviewer.firstName || ''} ${interviewer.lastName || ''}`.trim();
};
